import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { Mail, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { AuthErrorBanner } from '../components/AuthErrorBanner';
import { FieldError } from '../components/FieldError';

export default function LoginPage() {
  usePageTitle("Connexion à votre espace — Lodiss");

  const { lang, login, setPage, clearError, apiError, isLoading, currentUser, redirectAfterLogin } = useApp();

  const T = t[lang];

  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bannerError, setBannerError] = useState('');

  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('session_expired_message');
    if (sessionExpired === 'true') {
      sessionStorage.removeItem('session_expired_message');
      setBannerError(lang === 'fr'
        ? 'Votre session a expiré. Veuillez vous reconnecter.'
        : 'Your session has expired. Please log in again.');
    }
  }, [lang]);

  useEffect(() => {
    if (!isLoading && currentUser) {
      if (currentUser.role === 'super_admin') setPage('admin-dashboard');
      else if (currentUser.role === 'reseller') setPage('reseller-dashboard');
      else setPage('catalogue');
    }
  }, [currentUser, isLoading]);

  useEffect(() => {
    if (apiError) {
      const errorLower = apiError.toLowerCase();
      if (errorLower.includes('invalid') || errorLower.includes('incorrect') || errorLower.includes('password')) {
        setBannerError(lang === 'fr' 
          ? 'Identifiants incorrects. Vérifiez votre email et mot de passe' 
          : 'Incorrect credentials. Check your email and password');
      } else if (errorLower.includes('not active') || errorLower.includes('not verified') || errorLower.includes('pending') || errorLower.includes('email_not_verified')) {
        setBannerError(lang === 'fr'
          ? "Votre compte n'a pas encore été vérifié. Vérifiez votre email"
          : 'Your account has not been verified yet. Check your email');
        setTimeout(() => setPage('verify-email'), 1500);
      } else if (errorLower.includes('suspended') || errorLower.includes('banned')) {
        setBannerError(lang === 'fr'
          ? 'Votre compte a été suspendu. Contactez l\'administrateur'
          : 'Your account has been suspended. Contact the administrator');
      } else if (errorLower.includes('connection') || errorLower.includes('network') || errorLower.includes('serve')) {
        setBannerError(lang === 'fr'
          ? 'Une erreur est survenue. Veuillez réessayer'
          : 'An error occurred. Please try again');
      } else {
        setBannerError(apiError);
      }
    } else {
      setBannerError('');
    }
  }, [apiError, lang]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.email.trim() || !form.email.includes('@')) {
      e.email = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    }
    if (!form.password) {
      e.password = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    }
    return e;
  };

  const handleInputChange = (key: 'email' | 'password', value: string) => {
    setForm(f => ({ ...f, [key]: value }));
    setBannerError('');
    if (errors[key]) {
      setErrors(e => ({ ...e, [key]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    
    try {
      await login(form.email, form.password);
    } catch {
    }
  };

  const getTitle = () => {
    if (redirectAfterLogin?.startsWith('admin')) {
      return lang === 'fr' ? 'Connexion Admin' : 'Admin Login';
    }
    if (redirectAfterLogin?.startsWith('reseller')) {
      return lang === 'fr' ? 'Espace Revendeur' : 'Reseller Portal';
    }
    return lang === 'fr' ? 'Connexion' : 'Login';
  };

  const getSubtitle = () => {
    if (redirectAfterLogin?.startsWith('admin')) {
      return lang === 'fr' ? 'Accès à l\'administration LODISS' : 'LODISS admin access';
    }
    if (redirectAfterLogin?.startsWith('reseller')) {
      return lang === 'fr' ? 'Connectez-vous à votre espace revendeur' : 'Connect to your reseller portal';
    }
    return lang === 'fr' ? 'Accédez à votre compte LODISS' : 'Access your LODISS account';
  };

  return (
    <PageWrapper>
    <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
          <div className="mb-4">
            <button
              onClick={() => setPage('home')}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-lodiss-red transition-colors"
            >
              <ArrowLeft size={14} />
              {lang === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
            </button>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-950">
              {getTitle()}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {getSubtitle()}
            </p>
          </div>

          <AuthErrorBanner error={bannerError} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                {lang === 'fr' ? 'Email' : 'Email'}
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  disabled={isLoading}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    errors.email
                      ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                      : 'border-gray-200 focus:border-lodiss-red focus:ring-lodiss-red/20'
                  } ${isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="vous@entreprise.com"
                />
              </div>
              <FieldError message={errors.email} />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                {lang === 'fr' ? 'Mot de passe' : 'Password'}
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={form.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  disabled={isLoading}
                  className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    errors.password
                      ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                      : 'border-gray-200 focus:border-lodiss-red focus:ring-lodiss-red/20'
                  } ${isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="••••••••"
                />
              </div>
              <FieldError message={errors.password} />
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={() => setPage('forgot-password')}
                disabled={isLoading}
                className="text-sm text-lodiss-red hover:text-lodiss-dark font-medium disabled:opacity-50"
              >
                {lang === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {lang === 'fr' ? 'Se connecter' : 'Sign in'}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              {lang === 'fr' ? "Pas encore de compte ?" : "Don't have an account?"}{' '}
              <button
                onClick={() => setPage('register')}
                disabled={isLoading}
                className="text-lodiss-red font-semibold hover:underline disabled:opacity-50"
              >
                {lang === 'fr' ? 'Créer un compte' : 'Create an account'}
              </button>
            </p>
          </div>

          <div className="mt-4 text-center pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              {lang === 'fr' ? 'Vous êtes revendeur ?' : 'Are you a reseller?'}{' '}
              <button
                onClick={() => setPage('reseller')}
                disabled={isLoading}
                className="text-lodiss-red font-semibold hover:underline disabled:opacity-50"
              >
                {lang === 'fr' ? 'Devenir revendeur' : 'Become a reseller'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </main>
    </PageWrapper>
  );
}
